import { useEffect, useMemo, useState } from 'react';
import {
  Link as RouterLink,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { DateCalendar } from '@mui/x-date-pickers';
import { addDays, format, isSameDay, startOfDay } from 'date-fns';
import { fr as frLocale } from 'date-fns/locale';
import { providersService } from '../../shared/services/providers.service';
import {
  bookingsService,
  CANCELLATION_NOTICE_DAYS,
  CANCELLATION_REFUND_PERCENT,
} from '../../shared/services/bookings.service';
// Keep TS6133 quiet while the refund-policy box JSX is commented out for
// launch — these constants are still referenced by the disabled JSX. Drop
// this line when the policy box is re-enabled.
void [CANCELLATION_NOTICE_DAYS, CANCELLATION_REFUND_PERCENT];
import {
  availabilityService,
  type AvailabilitySlot,
} from '../../shared/services/availability.service';
import { ProviderInlineRating } from './ProviderInlineRating';
import { useAuth } from '../../shared/hooks/useAuth';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { ProviderRatingsCard } from './sidebar/ProviderRatingsCard';
import { OpeningHoursCard } from './sidebar/OpeningHoursCard';
import { type ProviderServiceItem, userDisplayName } from '../../shared/types';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// How far ahead to fetch the provider's availability — long enough to cover
// most planning horizons without paginating per month.
const AVAILABILITY_WINDOW_DAYS = 90;

function dayKey(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function formatPrice(cents?: number | null, currency?: string | null) {
  if (cents == null || !currency) return null;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

export function BookingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const { notify } = useNotification();
  const { translate } = useApiError();

  const dateLocale = i18n.language?.startsWith('fr') ? frLocale : undefined;

  const query = useQuery({
    queryKey: ['provider', id],
    queryFn: () => providersService.get(id),
    enabled: Boolean(id),
  });
  const p = query.data;

  // ── State ────────────────────────────────────────────────────────────
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [showCatalog, setShowCatalog] = useState(false);

  const today = startOfDay(new Date());
  /** Date picked on the calendar (drives the hour list below it). */
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  /** Availability slot id picked by the customer (single source of truth for the schedule). */
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  /**
   * Free-form datetime proposed by the customer when the provider has not
   * configured any availability slot. Value is the {@code <input type="datetime-local">}
   * native string (no timezone), converted to UTC ISO on submit.
   */
  const [freeDateTime, setFreeDateTime] = useState<string>('');

  // Fetch the provider's free slots for the next 90 days so the calendar
  // can highlight which days have availability.
  const fromIso = today.toISOString();
  const toIso = addDays(today, AVAILABILITY_WINDOW_DAYS).toISOString();
  const slotsQuery = useQuery({
    queryKey: ['provider-availability', id, fromIso, toIso],
    queryFn: () => availabilityService.listForProvider(id, fromIso, toIso),
    enabled: Boolean(id),
  });

  // Stabilize the empty-array fallback so memoized derivations downstream
  // don't re-run on every render while the query is still loading.
  const allSlots = useMemo(() => slotsQuery.data?.data ?? [], [slotsQuery.data?.data]);

  // Day keys that have at least one free slot — drives which days are
  // selectable on the calendar.
  const daysWithSlots = useMemo(() => {
    const out = new Set<string>();
    for (const s of allSlots) out.add(dayKey(new Date(s.startsAt)));
    return out;
  }, [allSlots]);

  // Free slots starting on the selected day, sorted by hour.
  const hoursForSelectedDate = useMemo(() => {
    if (!selectedDate) return [] as AvailabilitySlot[];
    return allSlots
      .filter((s) => isSameDay(new Date(s.startsAt), selectedDate))
      .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  }, [allSlots, selectedDate]);

  // Picking a different day clears any previously selected hour.
  useEffect(() => {
    setSelectedSlotId(null);
  }, [selectedDate]);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [touchedDetails, setTouchedDetails] = useState(false);

  const confirm = useMutation({
    mutationFn: () => {
      // Two booking modes:
      //   1. Slot-based — customer picked an availability slot ⇒ send
      //      {@code availabilitySlotId}; the server locks the slot and
      //      derives {@code preferredDateTime} from it.
      //   2. Free-date — provider has no slots configured ⇒ customer
      //      proposes their own datetime; the booking is created PENDING
      //      with {@code preferredDateTime} only. The provider can then
      //      confirm, reject, or counter-propose from their dashboard.
      const proposedIso = freeDateTime
        ? new Date(freeDateTime).toISOString()
        : undefined;
      return bookingsService.create(id, {
        customerName: name.trim(),
        customerEmail: email.trim(),
        message: message.trim() || undefined,
        providerServiceIds: selectedServiceIds,
        availabilitySlotId: selectedSlotId ?? undefined,
        preferredDateTime: selectedSlotId ? undefined : proposedIso,
      });
    },
    onSuccess: (booking) => {
      // Land on the confirmation page — gives the customer a clear "your
      // request was sent, you'll get an email" moment instead of dropping
      // them straight into their bookings list.
      navigate('/booking-submitted', {
        replace: true,
        state: {
          providerName: p?.businessName ?? '',
          providerId: p?.id,
          preferredDateTime: booking.preferredDateTime,
        },
      });
    },
    onError: (err) => notify.error(translate(err)),
  });

  // Pre-fill the name/email when the user is logged in.
  useEffect(() => {
    if (isAuthenticated && user) {
      setName((cur) => cur || userDisplayName(user));
      setEmail((cur) => cur || user.email);
    }
  }, [isAuthenticated, user]);

  // Pre-select a service from the URL (?serviceId=...). The id is the
  // provider_service row id, which uniquely addresses both catalog picks and
  // provider-defined custom services.
  useEffect(() => {
    const sid = searchParams.get('serviceId');
    if (!p || !sid) return;
    const match = p.services.find((s) => s.id === sid);
    if (match) {
      setSelectedServiceIds((cur) => (cur.includes(sid) ? cur : [sid]));
    }
  }, [p, searchParams]);

  // ── Loading / not found ──────────────────────────────────────────────
  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!p) return <Navigate to="/" replace />;

  // ── Derived ──────────────────────────────────────────────────────────
  const selectedServices: ProviderServiceItem[] = selectedServiceIds
    .map((sid) => p.services.find((s) => s.id === sid))
    .filter((s): s is ProviderServiceItem => Boolean(s));

  const remainingServices = p.services.filter((s) => !selectedServiceIds.includes(s.id));

  const selectedSlot = selectedSlotId
    ? (allSlots.find((s) => s.id === selectedSlotId) ?? null)
    : null;

  const emailValid = EMAIL_REGEX.test(email);
  const detailsValid = name.trim().length > 0 && emailValid;

  // A confirm is allowed when the customer has either picked a slot from the
  // provider's availability OR proposed their own datetime via the free
  // picker (used when the provider hasn't configured any slot).
  const hasFreeDate = freeDateTime.trim().length > 0
    && !Number.isNaN(new Date(freeDateTime).getTime())
    && new Date(freeDateTime).getTime() > Date.now();
  const canConfirm = selectedServices.length > 0
    && (selectedSlotId !== null || hasFreeDate)
    && detailsValid;

  const handleConfirm = () => {
    setTouchedDetails(true);
    if (!canConfirm) return;
    confirm.mutate();
  };

  const handleSlotPick = (slotId: string) => {
    setSelectedSlotId(slotId);
  };

  const handleAddService = (sid: string) => {
    setSelectedServiceIds((cur) => [...cur, sid]);
    setShowCatalog(false);
  };

  const handleRemoveService = (sid: string) => {
    setSelectedServiceIds((cur) => cur.filter((x) => x !== sid));
  };

  // Human-readable "when" for the recap card in step 3. Falls back to the
  // free-date input when the customer is in the "no availability" path.
  const formattedWhen = selectedSlot
    ? format(new Date(selectedSlot.startsAt), "EEEE d MMMM yyyy '•' HH:mm", {
        locale: dateLocale,
      })
    : hasFreeDate
      ? format(new Date(freeDateTime), "EEEE d MMMM yyyy '•' HH:mm", {
          locale: dateLocale,
        })
      : null;

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        {/* HEADER */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ mb: 0.75 }}>
            {p.businessName}
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ color: 'text.secondary' }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <LocationOnIcon sx={{ fontSize: '1rem' }} />
              <Typography
                component="a"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  [
                    p.businessName,
                    p.cityName ?? p.city?.name,
                    p.countryCode ?? p.city?.country,
                  ]
                    .filter(Boolean)
                    .join(', '),
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: '0.95rem',
                  color: 'text.secondary',
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {[p.cityName ?? p.city?.name, p.countryCode ?? p.city?.country]
                  .filter(Boolean)
                  .join(', ')}
              </Typography>
            </Stack>
            <ProviderInlineRating
              score={p.ratingAvg != null ? Number(p.ratingAvg) : null}
              count={p.ratingCount ?? null}
            />
            {/* "Vérifié" badge intentionally hidden — APPROVED only means
                admin-moderated for publication, not identity-verified. The
                proper verification flow (min tenure + admin review) lands
                later; until then we don't expose a false trust signal. */}
          </Stack>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            {/* STEP 1 — Selected services */}
            <SectionTitle index={1}>
              {selectedServices.length > 0 ? t('booking.step1Selected') : t('booking.step1Choose')}
            </SectionTitle>

            <Stack spacing={1.5} sx={{ mb: 1.5 }}>
              {selectedServices.length === 0 && !showCatalog && (
                <SectionCard sx={{ py: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary" sx={{ mb: 1.5 }}>
                    {t('booking.noServiceYet')}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setShowCatalog(true)}
                    startIcon={<AddRoundedIcon />}
                    sx={blackButtonSx}
                  >
                    {t('booking.addService')}
                  </Button>
                </SectionCard>
              )}

              {selectedServices.map((s) => (
                <SectionCard key={s.id}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ sm: 'center' }}
                    spacing={1.5}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700 }}>{s.serviceName}</Typography>
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{ mt: 0.25, color: 'text.secondary' }}
                        divider={
                          <Box
                            sx={{
                              width: 4,
                              height: 4,
                              borderRadius: '50%',
                              bgcolor: 'text.disabled',
                            }}
                          />
                        }
                      >
                        {s.durationMinutes && (
                          <Typography variant="body2">{s.durationMinutes} min</Typography>
                        )}
                        {formatPrice(s.customPriceCents, s.currency) && (
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: 'text.primary' }}
                          >
                            {s.priceType === 'STARTING_FROM'
                              ? `${t('provider.fromPrice')} ${formatPrice(s.customPriceCents, s.currency)}`
                              : formatPrice(s.customPriceCents, s.currency)}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                    <Link
                      component="button"
                      type="button"
                      onClick={() => handleRemoveService(s.id)}
                      underline="always"
                      sx={{ color: 'text.secondary', alignSelf: { xs: 'flex-end', sm: 'center' } }}
                    >
                      {t('booking.remove')}
                    </Link>
                  </Stack>
                </SectionCard>
              ))}
            </Stack>

            {/* Add another service trigger */}
            {selectedServices.length > 0 && !showCatalog && remainingServices.length > 0 && (
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={() => setShowCatalog(true)}
                sx={{ ...blackButtonSx, mb: 4 }}
              >
                {t('booking.addAnother')}
              </Button>
            )}

            {/* Catalog of remaining services */}
            {showCatalog && remainingServices.length > 0 && (
              <SectionCard sx={{ mb: 4, p: { xs: 2, sm: 2.5 } }}>
                <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
                    {t('booking.addAnother')}
                  </Typography>
                  <Link
                    component="button"
                    type="button"
                    onClick={() => setShowCatalog(false)}
                    underline="always"
                    sx={{ color: 'text.secondary' }}
                  >
                    {t('common.cancel')}
                  </Link>
                </Stack>
                <Stack divider={<Box sx={{ height: '1px', bgcolor: 'divider' }} />}>
                  {remainingServices.map((s) => (
                    <Stack
                      key={s.id}
                      direction={{ xs: 'column', sm: 'row' }}
                      alignItems={{ sm: 'center' }}
                      spacing={1.5}
                      sx={{ py: 1.5 }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600 }}>{s.serviceName}</Typography>
                        {s.description && (
                          <Typography variant="body2" color="text.secondary">
                            {s.description}
                          </Typography>
                        )}
                      </Box>
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{ color: 'text.secondary' }}
                      >
                        {s.durationMinutes && (
                          <Typography variant="body2">{s.durationMinutes} min</Typography>
                        )}
                        {formatPrice(s.customPriceCents, s.currency) && (
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: 'text.primary' }}
                          >
                            {s.priceType === 'STARTING_FROM'
                              ? `${t('provider.fromPrice')} ${formatPrice(s.customPriceCents, s.currency)}`
                              : formatPrice(s.customPriceCents, s.currency)}
                          </Typography>
                        )}
                      </Stack>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleAddService(s.id)}
                        sx={{ ...blackButtonSx, py: 0.5, px: 2 }}
                      >
                        {t('provider.select')}
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              </SectionCard>
            )}

            {/* STEP 2 — Date and time */}
            {selectedServices.length > 0 && (
              <>
                <SectionTitle index={2}>{t('booking.step2DateTime')}</SectionTitle>
                <SectionCard sx={{ p: 0, overflow: 'hidden', mb: 4 }}>
                  {slotsQuery.isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : daysWithSlots.size === 0 ? (
                    /* Provider has no configured availability — let the
                       customer propose their own datetime. The booking is
                       created PENDING with `preferredDateTime`; the
                       provider can confirm, reject, or counter-propose
                       from their dashboard. */
                    <Box sx={{ p: { xs: 2.5, md: 3 } }}>
                      <Typography
                        variant="overline"
                        sx={{
                          display: 'block',
                          color: 'text.secondary',
                          fontWeight: 700,
                          letterSpacing: '0.12em',
                          mb: 1,
                        }}
                      >
                        {t('booking.noAvailabilityTitle')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('booking.noAvailabilityHint')}
                      </Typography>
                      <TextField
                        type="datetime-local"
                        label={t('booking.proposeYourDateLabel')}
                        value={freeDateTime}
                        onChange={(e) => setFreeDateTime(e.target.value)}
                        fullWidth
                        required
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          // Earliest pickable = now. The browser enforces
                          // this in addition to our `hasFreeDate` check.
                          min: toLocalInputValue(new Date()),
                        }}
                        helperText={t('booking.proposeYourDateHelper')}
                      />
                    </Box>
                  ) : (
                    <Grid container>
                      {/* Calendar */}
                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                          borderRight: { md: '1px solid' },
                          borderBottom: { xs: '1px solid', md: 'none' },
                          borderColor: { xs: 'divider', md: 'divider' },
                        }}
                      >
                        <Box sx={{ p: { xs: 1, sm: 1.5 } }}>
                          <Typography
                            variant="overline"
                            sx={{
                              display: 'block',
                              color: 'text.secondary',
                              fontWeight: 700,
                              letterSpacing: '0.12em',
                              px: 1,
                              pt: 0.5,
                            }}
                          >
                            {t('booking.pickDate')}
                          </Typography>
                          <DateCalendar
                            value={selectedDate}
                            onChange={(d) => d && setSelectedDate(startOfDay(d))}
                            disablePast
                            maxDate={addDays(today, AVAILABILITY_WINDOW_DAYS - 1)}
                            shouldDisableDate={(d) => !daysWithSlots.has(dayKey(d))}
                          />
                        </Box>
                      </Grid>

                      {/* Hour chips */}
                      <Grid item xs={12} md={6}>
                        <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                          <Typography
                            variant="overline"
                            sx={{
                              display: 'block',
                              color: 'text.secondary',
                              fontWeight: 700,
                              letterSpacing: '0.12em',
                              mb: 1,
                            }}
                          >
                            {t('booking.pickHour')}
                          </Typography>
                          {!selectedDate ? (
                            <Typography variant="body2" color="text.secondary">
                              {t('booking.pickDate')}
                            </Typography>
                          ) : hoursForSelectedDate.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              {t('booking.noHoursForDay')}
                            </Typography>
                          ) : (
                            <Grid container spacing={1}>
                              {hoursForSelectedDate.map((slot) => {
                                const time = format(new Date(slot.startsAt), 'HH:mm');
                                const isPicked = selectedSlotId === slot.id;
                                return (
                                  <Grid item xs={4} sm={3} key={slot.id}>
                                    <Button
                                      fullWidth
                                      variant={isPicked ? 'contained' : 'outlined'}
                                      onClick={() => handleSlotPick(slot.id)}
                                      sx={{
                                        py: 1,
                                        fontWeight: 600,
                                        borderColor: 'divider',
                                        color: isPicked ? 'common.white' : 'text.primary',
                                        bgcolor: isPicked ? '#1A1816' : 'background.paper',
                                        '&:hover': {
                                          bgcolor: isPicked ? '#000' : 'action.hover',
                                          borderColor: isPicked ? '#000' : 'primary.main',
                                        },
                                      }}
                                    >
                                      {time}
                                    </Button>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  )}
                </SectionCard>
              </>
            )}

            {/* STEP 3 — Customer details. Visible as soon as a date has
                been chosen (either a provider slot or the free-date picker). */}
            {selectedServices.length > 0 && (selectedSlot || hasFreeDate) && (
              <>
                <SectionTitle index={3}>{t('booking.step3Details')}</SectionTitle>

                {/* Recap of when */}
                <SectionCard sx={{ mb: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {t('booking.whenLabel')}
                      </Typography>
                      <Typography sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                        {formattedWhen}
                      </Typography>
                    </Box>
                    <Link
                      component="button"
                      type="button"
                      onClick={() => {
                        // Reset whichever picker was in use so the customer
                        // can re-pick. selectedSlot path clears the slot;
                        // free-date path clears the typed datetime.
                        if (selectedSlot) setSelectedSlotId(null);
                        else setFreeDateTime('');
                      }}
                      underline="always"
                      sx={{
                        color: 'text.secondary',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <EditRoundedIcon sx={{ fontSize: '1rem' }} />
                      {t('booking.edit')}
                    </Link>
                  </Stack>
                </SectionCard>

                <SectionCard sx={{ mb: 4 }}>
                  {!isAuthenticated && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('booking.guestHint')}{' '}
                      <Link
                        component={RouterLink}
                        to="/login"
                        state={{ from: location }}
                        underline="always"
                        sx={{ color: 'primary.main', fontWeight: 600 }}
                      >
                        {t('booking.signInInstead')}
                      </Link>
                    </Typography>
                  )}

                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        label={t('booking.fieldName')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        fullWidth
                        error={touchedDetails && name.trim().length === 0}
                        helperText={
                          touchedDetails && name.trim().length === 0
                            ? t('booking.errorRequired')
                            : ' '
                        }
                      />
                      {!isAuthenticated && (
                        <TextField
                          label={t('booking.fieldEmail')}
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          fullWidth
                          error={touchedDetails && !emailValid}
                          helperText={
                            touchedDetails && !emailValid
                              ? email.trim().length === 0
                                ? t('booking.errorRequired')
                                : t('booking.errorInvalidEmail')
                              : ' '
                          }
                        />
                      )}
                    </Stack>
                    <TextField
                      label={t('booking.fieldMessage')}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      fullWidth
                      multiline
                      minRows={3}
                      placeholder={t('booking.fieldMessagePlaceholder')}
                      inputProps={{ maxLength: 500 }}
                      helperText={t('booking.fieldMessageHelper', {
                        count: message.trim().length,
                      })}
                    />
                  </Stack>
                </SectionCard>
              </>
            )}

            {/* DEFER-TO-PROVIDER NOTICE — launch-time stance: no automatic
                refund schedule, customer and provider agree directly. The
                refund-policy box below is kept commented out for re-enabling
                once the platform's cancellation/refund flow is finalised. */}
            {selectedServices.length > 0 && (
              <Box
                sx={{
                  mt: 4,
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                }}
              >
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: '0.12em', fontWeight: 700 }}
                >
                  {t('booking.deferPolicyTitle')}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {t('booking.deferPolicyHint')}
                </Typography>
              </Box>
            )}

            {/* CANCELLATION POLICY — disabled for launch. Re-enable once the
                refund schedule + provider payout flow is finalised.
            {selectedServices.length > 0 && (
              <Box
                sx={{
                  mt: 4,
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                }}
              >
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: '0.12em', fontWeight: 700 }}
                >
                  {t('booking.policyTitle')}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {t('booking.policyRefund', {
                    days: CANCELLATION_NOTICE_DAYS,
                    percent: CANCELLATION_REFUND_PERCENT,
                  })}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.25 }}>
                  {t('booking.policyNoRefund', { days: CANCELLATION_NOTICE_DAYS })}
                </Typography>
              </Box>
            )}
            */}

            {/* CONFIRM */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems="center"
              sx={{ mt: 4 }}
            >
              <Button
                component={RouterLink}
                to={`/providers/${p.id}`}
                variant="outlined"
                sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
              >
                {t('common.back')}
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                size="large"
                disabled={!canConfirm || confirm.isPending}
                onClick={handleConfirm}
                sx={{
                  ...blackButtonSx,
                  py: 1.5,
                  px: 4,
                  alignSelf: { xs: 'stretch', sm: 'auto' },
                }}
              >
                {confirm.isPending
                  ? t('common.loading')
                  : selectedSlot
                    ? t('booking.confirm')
                    : t('booking.sendRequest')}
              </Button>
            </Stack>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 2, textAlign: 'center' }}
            >
              {t('booking.privacyHint')}
            </Typography>
          </Grid>

          {/* SIDEBAR — ratings + opening hours */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: { md: 'sticky' }, top: { md: 88 } }}>
              <Stack spacing={2.5}>
                <ProviderRatingsCard providerId={p.id} />
                <OpeningHoursCard providerId={p.id} />
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

// ── Local presentational helpers ──────────────────────────────────────

const blackButtonSx = {
  bgcolor: '#1A1816',
  color: 'common.white',
  fontWeight: 700,
  boxShadow: 'none',
  '&:hover': { bgcolor: '#000', boxShadow: 'none' },
  '&.Mui-disabled': {
    bgcolor: 'action.disabledBackground',
    color: 'action.disabled',
  },
};

function SectionTitle({ index, children }: { index: number; children: React.ReactNode }) {
  return (
    <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
      <Box component="span" sx={{ color: 'primary.main', mr: 1 }}>
        {index}.
      </Box>
      {children}
    </Typography>
  );
}

function SectionCard({ children, sx }: { children: React.ReactNode; sx?: object }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

/** Format a {@code Date} (or anything {@code Date} accepts) as the
 *  {@code YYYY-MM-DDTHH:mm} string that {@code <input type="datetime-local">}
 *  uses for its {@code value} and {@code min} attributes. Goes through the
 *  **local** timezone so the displayed time matches what the user picks. */
function toLocalInputValue(d: Date): string {
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    d.getFullYear() +
    '-' + pad(d.getMonth() + 1) +
    '-' + pad(d.getDate()) +
    'T' + pad(d.getHours()) +
    ':' + pad(d.getMinutes())
  );
}
